import { Deal } from './Deal';
import { DealDebtSchedule } from './DealDebtSchedule';
import { DealRequestAllocation } from './DealRequestAllocation';
import { ProfileRealEstateType } from './DealRequestAllocationRealEstate';
import { Guid } from './Shared';
import { SourceUse } from './SourceUse';

export interface DataRoomLoanApplication {
	dealGuid: string;
	dataRoomGuid: string;
	effectiveDate: Date;
	col0: string;
	col1: string;
	col2: string;
	col3NoHtml: string;
	col4NoHtml: string;
	col5NoHtml: string;
	showBorrowerMetrics: boolean;
	showAcquisitionMetrics: boolean;
	transactionManager: string;
	secondaryTransactionManager: string;
	lenderRelationsManager: string;
	deal: Deal;
	allocation: DealRequestAllocation;
	financialMetrics: DealFinancialMetric[];
	debtSchedules: DealDebtSchedule[];
	excludedLenders: DealExcludedLender[];
	reducedFeeLenders: DealReducedFeeLender[];
	tasks: LoanApplicationTask[];
	sources: SourceUse[];
	uses: SourceUse[];
	isRawLead: boolean;
	isRawLeadVisible: boolean;
}

export interface DealExcludedLender {
	guid: Guid;
	dealGuid: Guid;
	bankGuid: Guid;
	bankName: string;
	isIncumbentLender: boolean;
}

export interface DealReducedFeeLender {
	guid: Guid;
	dealGuid: Guid;
	bankGuid: Guid;
	bankName: string;
}

export interface DealFinancialMetric {
	guid: string;
	dealGuid: string;
	analysisMetricType?: DealAnalysisMetricType;
	financialMetricType?: DealFinancialMetricType;
	metricName: string;
	twoYearsAgo?: number;
	previousFullYear?: number;
	trailingTwelveMonths?: number;
	projectedFullYear?: number;
	futureYear?: number;
	amountDescription?: string;
	profileRealEstateType?: ProfileRealEstateType;
}

export type DealFinancialMetricType = keyof typeof DealFinancialMetricTypeMap;
export const DealFinancialMetricTypeMap = {
	Cash: 'Cash & Marketables',
	AccountsReceivables: 'Accounts Receivable',
	Inventory: 'Inventory',
	RealEstate: 'Owner Occupied Real Estate',
	FurnitureFixture: 'Furniture / Fixtures',
	Equipment: 'Machinery & Equipment',
	Vehicles: 'Rolling Stock/Vehicles',
	MarineVessels: 'Marine Vessels',
	Helicopters: 'Helicopters',
	FixedWingAircraft: 'Fixed Wing Aircraft',
	Royalties: 'Royalty/Licensing Agreements',
	IntellectualProperty: 'Intellectual Property',
	TotalRevenue: 'Total Revenue',
	NetIncome: 'Net Income',
	Ebitda: 'EBITDA',
	Adjustments: 'EBITDA Adjustments',
	AdjustedEbitda: 'Adjusted EBITDA',
	Mrr: 'MRR (most recent period)',
};

export type DealAnalysisMetricType = keyof typeof DealAnalysisMetricTypeMap;
export const DealAnalysisMetricTypeMap = {
	Borrower: 'Borrower',
	Acquisition: 'Acquisition',
};

export interface DealPreferenceItem {
	index: number;
	propertyName: 'PricePriority' | 'CovenantPriority' | 'MaturityPriority' | 'AmortizationPriority' | 'AmountPriority';
	displayName: string;
}

export interface LoanApplicationTask {
	guid: string;
	applicationTaskTitle: string;
	collectionIndex: number | null;
	notApplicable: boolean;
	displayOrder: number | null;
	completedOn: Date | null;
	completedBy: string | null;
	status: ApplicationTaskStatusType;
	documents: LoanApplicationTaskDocument[];
	createdOn: string;
	summary: string;
	loanApplicationDisplayOrder: number;
	statusValue: number;
}

export type ApplicationTaskStatusType = keyof typeof ApplicationTaskStatusTypeMap;
export const ApplicationTaskStatusTypeMap = {
	Pending: 'Pending',
	InProgress: 'In Progress',
	Completed: 'Completed',
	NotApplicable: 'N/A',
};

export interface LoanApplicationTaskDocument {
	guid: string;
	documentTitle: string;
	fileName: string;
	createdOn: Date;
	fileSize: number;
}

export interface LoanApplicationTaskTemplate {
	guid: string;
	title: string;
	displayOrder: number;
	isLoanApplicationDefault: boolean;
	isCustomFolder: boolean;
}
